import React, { useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import { ERROR_401 } from "config/errorPage";

import { useInstitutionForm } from "hooks/institutions/useInstitutionForm";
import { useCountries } from "hooks/useCountries";
import { useInstitutionPermissions } from "hooks/institutions/useInstitutionPermissions";

import InstitutionPageHeader from "components/institutions/InstitutionPageHeader";
import InstitutionFormFields from "components/institutions/InstitutionFormFields";
import FormActions from "components/institutions/FormActions";
import LoadingTable from "views/components/LoadingTable";

const FilialeInstitutionForm = ({ userPermissions }) => {
  const { t } = useTranslation();

  const permissions = useInstitutionPermissions(userPermissions);

  useEffect(() => {
    if (!permissions.canUpdateInstitution) {
      window.location.href = ERROR_401;
    }
  }, [permissions.canUpdateInstitution]);

  const {
    data,
    errors,
    loading: formLoading,
    submitting,
    countries: countriesData,
    institutionCountry,
    updateField,
    handleFileUpload,
    submitForm,
  } = useInstitutionForm();

  const { countries, selectedCountry, updateSelectedCountry } = useCountries(
    countriesData,
    institutionCountry
  );

  const handleFieldChange = useCallback(
    (fieldName, value) => {
      updateField(fieldName, value);
    },
    [updateField]
  );

  const handleCountryChange = useCallback(
    (selectedOption) => {
      updateSelectedCountry(selectedOption);
      if (selectedOption) {
        updateField("country_id", selectedOption.value);
      } else {
        updateField("country_id", "");
      }
    },
    [updateSelectedCountry, updateField]
  );

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (file) {
        handleFileUpload(file);
      }
    },
    [handleFileUpload]
  );

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      submitForm();
    },
    [submitForm]
  );

  if (!permissions.canUpdateInstitution) {
    return null;
  }

  if (formLoading) {
    return (
      <div className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor">
        <InstitutionPageHeader
          title={t("Paramètres")}
          subtitle={t("Modification")}
        />
        <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
          <LoadingTable />
        </div>
      </div>
    );
  }

  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <InstitutionPageHeader
        title={t("Paramètres")}
        subtitle={t("Modification")}
      />

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <div className="row">
          <div className="col">
            <div className="kt-portlet">
              <div className="kt-portlet__head">
                <div className="kt-portlet__head-label">
                  <h3 className="kt-portlet__head-title">
                    {t("Modification d'une institution")}
                  </h3>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="kt-form">
                <div className="kt-portlet__body">
                  <div className="tab-content">
                    <div
                      className="tab-pane active"
                      id="kt_user_edit_tab_1"
                      role="tabpanel"
                    >
                      <InstitutionFormFields
                        data={data}
                        errors={errors}
                        countries={countries}
                        selectedCountry={selectedCountry}
                        onFieldChange={handleFieldChange}
                        onCountryChange={handleCountryChange}
                        onFileChange={handleFileChange}
                      />

                      <FormActions
                        onSubmit={handleSubmit}
                        isSubmitting={submitting}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    userPermissions: state.user.user.permissions,
  };
};

export default connect(mapStateToProps)(FilialeInstitutionForm);
